import { GridColumn } from '@progress/kendo-react-grid';
import PQueue from 'p-queue'
import * as React from 'react';
import { atom, useRecoilValue } from "recoil";
import { queryNoTracking, update } from '../../services/DataService';
import { accentUtils, enqueue, from, insyteDate, memoize, t } from '../../services/HelperService';
import { RTLQueryFetcher } from '../RTLFetch';



// Never debounced: the shared QueryResult debounce coalesces concurrent calls with different keys into one
// request, so several grids mounting together would all receive (and then persist) one grid's settings.
const query_Grid_Settings = queryNoTracking("Grid_Settings", 0);


export const getLastPage = memoize(gridID => atom({
    key: `gridLastPage_${gridID}`,
    default: null,
}));


export const getAdhocFilter = memoize(gridID => atom({
    key: `gridAdhocFilter_${gridID}`,
    default: null,
}));


/**
 * Atom effect that persists a grid setting when the atom is set by the UI.
 *
 * Recoil (0.7) also invokes onSet when an atom's async default resolves: the atom is marked modified and the
 * transaction subscriber calls the handler with isReset === true (no value in the tree yet). Every persisted
 * grid atom has a Promise default, so without this guard each grid issued one SaveGridSetting per atom on
 * mount - and, combined with the settings lookup race, wrote another grid's settings under its own key.
 */
const persistGridSetting = (gridID, toPatch) => ({ onSet }) => {
    onSet((newValue, oldValue, isReset) => {
        if (isReset) return;

        // Patches are coalesced per grid and skipped when unchanged inside GridSettingsManager.
        gridSettingsManager.updateGridSettings(gridID, toPatch(newValue));
    });
};


export const getAdvancedFilter = memoize(gridID => atom({
    key: `gridAdvancedFilter_${gridID}`,
    default: new Promise(resolve => gridSettingsManager.getSettings(gridID).then(s => resolve(s?.SelectedFilter))),
    effects: [
        persistGridSetting(gridID, advancedFilter => ({ SelectedFilter: advancedFilter }))
    ]
}));

export const getQuickFilters = memoize(gridID => atom({
    key: `gridQuickFilters_${gridID}`,
    default: new Promise(resolve => gridSettingsManager.getSettings(gridID).then(s => resolve(s?.QuickFilters))),
    effects: [
        persistGridSetting(gridID, quickFilters => ({ QuickFilters: accentUtils.isNull(quickFilters) ? [] : quickFilters }))
    ]
}));


export const getGridGroup = memoize(gridID => atom({
    key: `gridGridGroup_${gridID}`,
    default: new Promise(resolve => gridSettingsManager.getSettings(gridID).then(s => resolve(s?.Group))),
    effects: [
        persistGridSetting(gridID, group => ({ Group: group }))
    ]
}));


export const getGridFilter = memoize(gridID => atom({
    key: `gridGridFilter_${gridID}`,
    default: null,
}));


export const getGridSearch = memoize(gridID => atom({
    key: `gridGridSearch_${gridID}`,
    default: null,
}));


export const getGridSort = memoize(gridID => atom({
    key: `gridGridSort_${gridID}`,
    default: new Promise(resolve => gridSettingsManager.getSettings(gridID).then(s => resolve(s?.Sort))),
    effects: [
        persistGridSetting(gridID, sort => ({ Sort: sort }))
    ]
}));


export const getGridColumnsWidths = memoize(gridID => atom({
    key: `gridGridColumnsWidth_${gridID}`,
    default: new Promise(resolve => gridSettingsManager.getSettings(gridID).then(s => resolve(s?.ColumnWidths ?? {}))),
    effects: [
        persistGridSetting(gridID, widths => ({ ColumnWidths: widths }))
    ]
}));

export const getGridHiddenColumns = memoize(gridID => atom({
    key: `gridGridHiddenColumns_${gridID}`,
    default: new Promise(resolve => gridSettingsManager.getSettings(gridID).then(s => resolve(s?.HiddenColumns ?? []))),
    effects: [
        persistGridSetting(gridID, hidden => ({ HiddenColumns: hidden }))
    ]
}));


export const getGridDetails = memoize(gridID => atom({
    key: `gridGridDetails_${gridID}`,
    default: false
}));

export const getGridExpandedItems = memoize(gridID => atom({
    key: `gridGridExpandedItems_${gridID}`,
    default: {}
}));

export const getGridSelectedItems = memoize(gridID => atom({
    key: `gridGridSelectedItems_${gridID}`,
    default: {}
}));


export function processDataForExport(data, columns, detailsField, processExport){


    let cols = columns.filter(c => c.field !== "selected" && c.field !== "action");

    if (!accentUtils.isEmpty(detailsField)) {
        cols.push(<GridColumn field={detailsField} title={detailsField} />);
    }

    
    var dateCols = from(columns).where(c => c.filter === "date").toDictionary(c => c.field);
    // InsyteDate columns hold "yyyy-MM-dd"; build the Excel date from the calendar parts (local midnight)
    // rather than new Date(string), which would parse as UTC midnight and shift a day.
    var insyteDateCols = from(columns).where(c => c.filter === "insytedate").toDictionary(c => c.field);
        

    data.map(function (rec) {

        var dateFields = from(Object.keys(rec)).where(f => dateCols.contains(f)).toArray();
        var insyteDateFields = from(Object.keys(rec)).where(f => insyteDateCols.contains(f)).toArray();


        Object.keys(rec).map(f => {

            


            if (!accentUtils.isEmpty(rec[f])) {

                if (dateFields.indexOf(f) !== -1) {
                    rec[f] = new Date(rec[f]);
                } else if (insyteDateFields.indexOf(f) !== -1) {
                    rec[f] = insyteDate.toJsDate(rec[f]);
                } else {

                    if (!Array.isArray(rec[f])) {
                        if ((typeof (rec[f]) === "boolean")) {
                            rec[f] = rec[f] ? "Yes" : "No";
                        } else if (typeof rec[f] !== "number") {
                            rec[f] = t(rec[f]);
                        }
                    }
                }
            }

            if (processExport) {
                rec[f] = processExport(f, rec[f]);
            }

        });


    });


    return {
        data: data,
        cols: cols.map(c => {
            if (!accentUtils.isNull(c.format)) {
                c.cellOptions = {
                    format: c.format,
                };
            }
            return c;
        })
    };




};


let pagerVersion = 0;

const PAGE_SIZE = 40;

class GridPager {

    constructor(
        gridFilterState,
        query,
        customHeaders,
        onRefresh,
        initialPages = 1
    ) {
        this.queue = new PQueue({ concurrency: 1 });

        this.initialPageSize = (accentUtils.isNull(initialPages) ? 1 : initialPages) * PAGE_SIZE;
        this.refreshFired = false;
        this.onRefresh = onRefresh;
        this.total = 0;
        this.pages = {};
        this.fetchStarted = {};

        pagerVersion++;

        this.version = pagerVersion;
        this.lastPage = 0;

        this.adhocFilter = gridFilterState.adhocFilter;
        this.advancedFilter = gridFilterState.advancedFilter;
        this.quickFilters = gridFilterState.quickFilters;
        this.gridFilter = gridFilterState.gridFilter;
        this.gridSearch = gridFilterState.gridSearch;
        this.gridSort = gridFilterState.gridSort;
        this.gridGroup = gridFilterState.gridGroup;
        this.query = query;
        this.customHeaders = customHeaders;
        this.fetchNext = this.fetchNext.bind(this);
        this.getData = this.getData.bind(this);
        this.getTotal = this.getTotal.bind(this);
        this.getLoaded = this.getLoaded.bind(this);
        this.canFetch = this.canFetch.bind(this);
        this.refresh = this.refresh.bind(this);
        this.updateDataItem = this.updateDataItem.bind(this);
        this.getLastPage = this.getLastPage.bind(this);
    }


    getLastPage() {
        return this.lastPage;
    }

    getTotal() {
        return this.total;
    }

    getData() {        
        return from(Object.keys(this.pages)).orderBy(k => k).selectMany(k => this.pages[k]?.data ?? []).toArray();
    }


    getLoaded() {
        return from(Object.keys(this.pages)).sum(k => this.pages[k]?.data?.length ?? 0);
    }

    canFetch(pageToFetch) {

        const maxPages = Math.ceil(this.getTotal() / PAGE_SIZE);

        
        const alreadyFetchedOrFetching = !accentUtils.isNull(this.pages[pageToFetch]) || this.fetchStarted[pageToFetch];


        return !alreadyFetchedOrFetching && (accentUtils.isNull(this.pages[1]) || pageToFetch <= maxPages);
    }


    updateDataItem(updatedItem) {


        for (const pageNo of Object.keys(this.pages)) {

            const page = this.pages[pageNo];

            const index = from(page.data).indexOf(i => i.ID === updatedItem.ID);

            if (index !== -1) {
                page.data[index] = updatedItem;
                break;
            }

        }


    }

    refresh() {
        this.refreshFired = false;
        this.pages = {};
        this.fetchStarted = {};
        return this.fetchNext(1, true);
    }


    async doFetch(page, queryFetcher, dataStateFilter, done, bustCache) {

        try {

            const res = await queryFetcher.fetchData(dataStateFilter, bustCache);

            this.pages[page] = res;
            this.total = res.total;


            done(page);

            const me = this;

            window.setTimeout(() => {
                if (!me.refreshFired) {
                    me.refreshFired = true;
                    me.onRefresh();
                }
            }, 0);


        } catch (ex) {

            done(page);

        }
        


    }

    fetchNext(page, bustCache) {


        const requireLoad = this.canFetch(page);

        if (!requireLoad) return Promise.resolve(null);

        this.lastPage = page;
        this.fetchStarted[page] = true;

        const dataStateFilter = {
            filter: this.gridFilter,
            skip: PAGE_SIZE * (page - 1),
            take: page === 1 ? this.initialPageSize : PAGE_SIZE,
            sort: this.gridSort,
            customHeaders: {
                adhocFilter: accentUtils.isNull(this.adhocFilter) ? null : JSON.stringify({ filter: this.adhocFilter }),
                advancedFilter: accentUtils.isNull(this.advancedFilter) ? null : this.advancedFilter.ID,
                quickFilters: (this.quickFilters?.length ?? 0) === 0 ? null : this.quickFilters.filter(x=>x.Selected).map(x=>x.ID),
                customQuery: accentUtils.isNull(this.customHeaders) ? null : JSON.stringify(this.customHeaders()),
                group: (this.gridGroup?.length ?? 0) === 0 ? null : JSON.stringify(this.gridGroup)
            }
        };



        const queryFetcher = new RTLQueryFetcher({
            query: this.query(this.gridSearch)
        });


        return new Promise(resolve => {
            this.queue.add(() => this.doFetch(page, queryFetcher, dataStateFilter, resolve, bustCache));
        });

    }



    async fetchExport() {


        const dataStateFilter = {
            filter: this.gridFilter,
            sort: this.gridSort,
            customHeaders: {
                adhocFilter: accentUtils.isNull(this.adhocFilter) ? null : JSON.stringify({ filter: this.adhocFilter }),
                advancedFilter: accentUtils.isNull(this.advancedFilter) ? null : this.advancedFilter.ID,
                quickFilters: (this.quickFilters?.length ?? 0) === 0 ? null : this.quickFilters.filter(x => x.Selected).map(x => x.ID),
                customQuery: accentUtils.isNull(this.customHeaders) ? null : JSON.stringify(this.customHeaders()),
                group: (this.gridGroup?.length ?? 0) === 0 ? null : JSON.stringify(this.gridGroup)
            }
        };



        const queryFetcher = new RTLQueryFetcher({
            query: this.query(this.gridSearch)
        });


        var result = await queryFetcher.fetchData(dataStateFilter);


        if (Array.isArray(result)) {
            return result;
        }

        return result.data;

    }


}




export const useGridFilterState = (gridID) => {

    const adhocFilter = useRecoilValue(getAdhocFilter(gridID));
    const advancedFilter = useRecoilValue(getAdvancedFilter(gridID));
    const quickFilters = useRecoilValue(getQuickFilters(gridID));
    const gridFilter = useRecoilValue(getGridFilter(gridID));
    const gridSearch = useRecoilValue(getGridSearch(gridID));
    const gridSort = useRecoilValue(getGridSort(gridID));
    const gridGroup = useRecoilValue(getGridGroup(gridID));


    const state = React.useMemo(() => {
        return {
            adhocFilter: adhocFilter,
            advancedFilter: advancedFilter,
            quickFilters: quickFilters,
            gridFilter: gridFilter,
            gridSearch: gridSearch,
            gridSort: gridSort,
            gridGroup: gridGroup
        }
    }, [adhocFilter, advancedFilter, quickFilters, gridFilter, gridSearch, gridSort, gridGroup]);


    React.useEffect(() => { }, []);


    return state;

};


export const useGridData = (gridID, query, customHeaders, onRefresh, initialPages) => {


    const gridFilterState = useGridFilterState(gridID);


    const stackCount = React.useRef(0);

    const pager = React.useMemo(
        () => new GridPager(gridFilterState, query, customHeaders, onRefresh, initialPages),
        [gridFilterState, query, customHeaders]
    );

    const [loading, setLoading] = React.useState(true);
    const [, setDataVersion] = React.useState(0);


    const onBeforeDataChange = React.useCallback(() => {

        stackCount.current++;
        setLoading(true);

    }, [stackCount, setLoading]);


    const onAfterDataChanged = React.useCallback(() => {

        stackCount.current = Math.max(0, --stackCount.current);
        if (stackCount.current == 0) {
            setLoading(false);
        }

        // The grid data lives in the mutable pager, so a re-render must be forced here.
        // Relying on the loading flag alone fails when a fetch resolves synchronously
        // (e.g. array-backed queries): both setLoading calls land in the same batch,
        // no state changes, and React bails out without committing the new rows.
        setDataVersion(v => v + 1);

    }, [stackCount, setLoading, setDataVersion]);

    const updateDataItem =  React.useCallback(updatedItem => {

        if (pager) {

            onBeforeDataChange();

            pager.updateDataItem(updatedItem);

            onAfterDataChanged();

        }

    }, [pager, onBeforeDataChange, onAfterDataChanged]);

    const refresh = React.useCallback(async () => {
        if (pager) {

            onBeforeDataChange();

            await pager.refresh();

            onAfterDataChanged();


        }

    }, [pager, onBeforeDataChange, onAfterDataChanged]);
    
    
    const fetchNext = React.useCallback(async (page) => {
        if (pager && pager.canFetch(page)) {


            onBeforeDataChange();

            await pager.fetchNext(page);



            onAfterDataChanged();

        }


    }, [pager, onBeforeDataChange, onAfterDataChanged]);


    const fetchExport = React.useCallback(async () => {

        onBeforeDataChange();

        const exportData = await pager.fetchExport();




        onAfterDataChanged();

        return exportData;


    }, [pager, onBeforeDataChange, onAfterDataChanged])
   



    




    React.useEffect(() => {
        fetchNext(1);
    }, [pager]);
    

    return {
        version: pager.version,
        loading: loading,
        lastPage: pager.lastPage,
        data: pager.getData(),
        total: pager.getTotal(),
        fetchPage: (p) => {
            fetchNext(p);
        },
        fetchExport: () => {
            return fetchExport();
        },
        refresh: async () => {
            refresh();
        },
        updateDataItem: updatedItem => {
            updateDataItem(updatedItem);
        }
    };

    

};



// A setting with no effect on the grid: absent, null, false, or an empty list / object. Every consumer reads
// settings with `??` / `?.`, so dropping these before saving changes nothing on load.
const isEmptySetting = v =>
    accentUtils.isNull(v)
    || v === false
    || (Array.isArray(v) && v.length === 0)
    || (typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0);

const normaliseSettings = s =>
    Object.fromEntries(Object.entries(s ?? {}).filter(([, v]) => !isEmptySetting(v)));

// Window in which patches from several atoms (e.g. the five fired by Reset Layout, or hide-column-then-sort)
// are folded into one SaveGridSetting.
const SAVE_COALESCE_MS = 250;


class GridSettingsManager {

    constructor() {
        this.data = {};
        // Keys whose settings have been checked against the grid's columns this session.
        this.sanitised = {};
        // Key -> JSON of the normalised settings the server currently holds. "{}" means no row.
        this.lastSaved = {};
        // Key -> pending coalesced save timer.
        this.saveTimers = {};

        this.resetGridSettings = this.resetGridSettings.bind(this);
        this.updateGridSettings = this.updateGridSettings.bind(this);
        this.getSettings = this.getSettings.bind(this);
        this.scheduleSave = this.scheduleSave.bind(this);
    }


    async resetGridSettings(gridID) {

        const key = gridID;

        // Drop any pre-reset save still waiting, otherwise it would recreate the row after the delete.
        clearTimeout(this.saveTimers[key]);
        delete this.saveTimers[key];

        await update("ResetGridSettings", { Key: key });

        // Seed the cache with the now-empty row rather than clearing it: the atom resets that follow each
        // persist a patch, and a refetch here could race a save still sitting in the shared queue.
        this.data[key] = {};
        this.sanitised[key] = true;
        this.lastSaved[key] = "{}";
    }

    async updateGridSettings(key, e) {
        
        if (key && key !== 'undefined') {


            const loaded = await this.getSettings(key);

            // Read the cache synchronously after the await: several atoms patch the same key in one tick
            // (e.g. Reset Layout), so the value returned by getSettings may already be stale. Once getSettings
            // has resolved the cache holds a plain object, so no further await is needed between read and write.
            const current = this.data[key];
            const settings = accentUtils.isNull(current) || typeof current.then === "function" ? loaded : current;
            this.data[key] = { ...settings, ...e };

            this.scheduleSave(key);

        }

    }


    /**
     * Saves this.data[key] once the coalesce window closes, and only if the normalised settings differ from
     * what the server already holds. After Reset Layout the atom resets patch in {} / [] / null values, which
     * normalise to nothing, so no save is issued and the deleted row is not recreated.
     */
    scheduleSave(key) {

        clearTimeout(this.saveTimers[key]);

        this.saveTimers[key] = setTimeout(() => {

            delete this.saveTimers[key];

            const current = this.data[key];
            if (accentUtils.isNull(current) || typeof current.then === "function") return;

            const toSave = normaliseSettings(current);
            const json = JSON.stringify(toSave);

            if (json === (this.lastSaved[key] ?? "{}")) return;

            this.lastSaved[key] = json;

            enqueue(() => update("SaveGridSetting", { Key: key, Options: toSave }));

        }, SAVE_COALESCE_MS);

    }



    /**
     * Settings for one grid. The in-flight promise is cached per key so the several atoms of a grid share a
     * single request while different grids can never be handed each other's row. When validFields is supplied
     * (the grid's column fields) any Sort / Group / HiddenColumns / ColumnWidths entry for a field the grid does
     * not have is dropped and the cleaned settings re-saved - repairs rows corrupted by earlier cross-grid saves.
     */
    async getSettings(key, validFields) {

        if (accentUtils.isEmpty(key)) return null;


        if (accentUtils.isNull(this.data[key])) {

            this.data[key] = query_Grid_Settings.getFirstOrDefault({ key: key })
                .then(row => {
                    if (!accentUtils.isNull(row) && !accentUtils.isEmpty(row.Key) && row.Key !== key) {
                        // Defensive: never accept another grid's settings under this key.
                        return {};
                    }
                    return accentUtils.isNull(row?.Settings) ? {} : JSON.parse(row.Settings);
                })
                .then(settings => {
                    this.data[key] = settings;
                    // What the server holds right now; later saves are skipped when they would not change it.
                    this.lastSaved[key] = JSON.stringify(normaliseSettings(settings));
                    return settings;
                })
                .catch(() => {
                    delete this.data[key];
                    return {};
                });

        }

        const settings = await this.data[key];

        // Sanitise once per key, the first time a caller can tell us which fields the grid has.
        if (Array.isArray(validFields) && validFields.length > 0 && !this.sanitised[key]) {
            this.sanitised[key] = true;
            const cleaned = this.sanitise(key, settings, validFields);
            this.data[key] = cleaned;
            return cleaned;
        }

        return settings;

    }


    sanitise(key, settings, validFields) {

        if (accentUtils.isNull(settings) || !Array.isArray(validFields) || validFields.length === 0) return settings;

        const valid = new Set(validFields);
        const cleaned = { ...settings };
        let changed = false;

        const cleanFieldList = (list, getField) => {
            if (!Array.isArray(list)) return list;
            const kept = list.filter(x => valid.has(getField(x)));
            if (kept.length !== list.length) changed = true;
            return kept;
        };

        cleaned.Sort = cleanFieldList(cleaned.Sort, x => x?.field);
        cleaned.Group = cleanFieldList(cleaned.Group, x => x?.field);
        cleaned.HiddenColumns = cleanFieldList(cleaned.HiddenColumns, x => x);

        if (!accentUtils.isNull(cleaned.ColumnWidths) && typeof cleaned.ColumnWidths === "object") {
            const entries = Object.entries(cleaned.ColumnWidths).filter(([field]) => valid.has(field));
            if (entries.length !== Object.keys(cleaned.ColumnWidths).length) {
                cleaned.ColumnWidths = Object.fromEntries(entries);
                changed = true;
            }
        }

        if (changed) {
            // getSettings stores `cleaned` in this.data[key] as soon as this returns; the coalesced save picks
            // it up from there and goes through the same skip-if-unchanged check as every other save.
            this.scheduleSave(key);
        }

        return cleaned;

    }


}

export const gridSettingsManager = new GridSettingsManager();


